import { renderBass as renderKnockBuffer } from '../bass/bassVoice';
/** Client-side rendering for Knock Engine clips. */
import { getCached, putBuffer } from './BufferCache';
import { isAudioEligible } from './classAudio';

export const KNOCK_SOUND_TYPES = {
  'Sub Sine': { oscType: 'sine', filterFreq: 300, detune: 0 },
  'Sawtooth Grit': { oscType: 'sawtooth', filterFreq: 500, detune: 0 },
  'Warm Round': { oscType: 'sine', filterFreq: 200, detune: 8 },
  'Square Punch': { oscType: 'square', filterFreq: 400, detune: 0 },
  'Triangle Soft': { oscType: 'triangle', filterFreq: 600, detune: 0 },
};

const DEFAULT_SOUND = KNOCK_SOUND_TYPES['Sub Sine'];
// Signature of what each cached Knock buffer was rendered from. Tempo alone is not
// enough: regenerating a variation or undoing one keeps the clip's loop_id, so a
// tempo-only check served the old bassline after the pattern had changed.
const renderedSignatures = new Map();

export function soundSettings(nameOrSettings) {
  if (nameOrSettings && typeof nameOrSettings === 'object') {
    return { ...DEFAULT_SOUND, ...Object.fromEntries(Object.entries(nameOrSettings)
      .filter(([, value]) => value !== undefined && value !== null)) };
  }
  return { ...(KNOCK_SOUND_TYPES[nameOrSettings] || DEFAULT_SOUND) };
}

export { renderBass as renderKnockBuffer } from '../bass/bassVoice';

/** Render missing/stale Knock clip buffers at the current session tempo. */
export async function ensureKnockBuffers(tracks, bpm) {
  const jobs = [];
  // Sliced Knock clips share one loop_id and one buffer; render it once per call.
  const queued = new Set();
  for (const track of tracks || []) {
    if (!isAudioEligible(track)) continue;
    for (const clip of track.clips || []) {
      if (!clip.isKnockEngine) continue;
      const settings = soundSettings({
        ...soundSettings(clip.knockSoundType),
        oscType: clip.knockOscType,
        filterFreq: clip.knockFilterFreq,
        detune: clip.knockDetune,
      });
      const signature = JSON.stringify([Number(bpm), clip.naturalLoopBars || 4, clip.knockVolume ?? 0.8, clip.knockSampleId || null,
        settings.oscType, settings.filterFreq, settings.detune, clip.knockPattern || []]);
      if (getCached(clip.loop_id) && renderedSignatures.get(clip.loop_id) === signature) continue;
      if (queued.has(clip.loop_id)) continue;
      queued.add(clip.loop_id);
      jobs.push(renderKnockBuffer({ pattern: clip.knockPattern || [], bpm, bars: clip.naturalLoopBars || 4,
        oscType: settings.oscType, filterFreq: settings.filterFreq, detune: settings.detune,
        volume: clip.knockVolume ?? 0.8, sampleId: clip.knockSampleId }).then((buffer) => {
        putBuffer(clip.loop_id, buffer);
        renderedSignatures.set(clip.loop_id, signature);
      }));
    }
  }
  await Promise.all(jobs);
}
